import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  SessionLayoutSwitchDirection,
  SessionLayoutSwitchSource,
} from '@/models/analytics/features/session-layout';

/**
 * The concrete thing a user did to the V2 session-layout opt-in that results in
 * a layout switch. Call sites pass one of these instead of a raw boolean so the
 * intent is explicit and the mapping to event props lives in one place. An
 * invite decline is not a switch, so it has no entry here and emits nothing.
 */
export type SessionLayoutSwitch =
  | 'labs_enabled' // Heidi Labs toggle ON  -> switched to V2
  | 'labs_disabled' // Heidi Labs toggle OFF -> switched back to V1
  | 'account_menu_enabled' // Avatar-menu toggle ON  -> switched to V2
  | 'account_menu_disabled' // Avatar-menu toggle OFF -> switched back to V1
  | 'labs_invite_card_accepted'
  | 'ga_invite_card_accepted'
  | 'ga_invite_banner_accepted';

const SWITCHES: Record<
  SessionLayoutSwitch,
  { direction: SessionLayoutSwitchDirection; source: SessionLayoutSwitchSource }
> = {
  labs_enabled: { direction: 'v1_to_v2', source: 'labs_settings' },
  labs_disabled: { direction: 'v2_to_v1', source: 'labs_settings' },
  account_menu_enabled: { direction: 'v1_to_v2', source: 'account_menu' },
  account_menu_disabled: { direction: 'v2_to_v1', source: 'account_menu' },
  labs_invite_card_accepted: { direction: 'v1_to_v2', source: 'invite_card_labs' },
  ga_invite_card_accepted: { direction: 'v1_to_v2', source: 'invite_card_ga' },
  ga_invite_banner_accepted: { direction: 'v1_to_v2', source: 'invite_banner' },
};

/**
 * Emits `session_layout_switched` when a user changes the Scribe session layout,
 * shared by the Heidi Labs settings toggle, account menu, invite cards, and
 * persistent invite banner.
 *
 * `direction` is the explicit intent analysts query: `v2_to_v1` is a switch
 * back, `v1_to_v2` a switch on. `source` is the entry point. The event fires
 * only on a real switch, so `direction` is always present.
 *
 * Routing is Snowplow-first. Snowplow forwards the event to Amplitude, so the
 * direct Amplitude destination stays disabled to avoid double-counting.
 */
export function trackSessionLayoutSwitch(change: SessionLayoutSwitch): void {
  track({
    event_name: TrackingEventName.SessionLayoutSwitched,
    props: SWITCHES[change],
    options: { amplitude: false },
  });
}
